import { inject, Signal } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { EntityProps, EntityState, setAllEntities, setEntity, updateEntity } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';
import { pipe, switchMap } from 'rxjs';
import { v7 as uuidv7 } from 'uuid';

import { Response } from '../../shared/models/response.model';
import { SelectedEntityState } from '../../shared/store/selected-entity.feature';
import { Connecteur } from '../models/connecteur.model';
import { FinParcours } from '../models/fin-parcours.model';
import { Jalon } from '../models/jalon.model';
import { Parcours } from '../models/parcours.model';
import { RéférentielMétierParcoursService } from '../services/référentiel-métier-parcours.service';
import { GroupeÉtapesÀLaCarteStore } from './groupe-étapes-à-la-carte/groupe-étapes-à-la-carte.store';
import { ParcoursState } from './parcours.state';

export function avecMéthodesParcours<_>() {
  return signalStoreFeature(
    {
      state: type<ParcoursState & EntityState<Parcours> & SelectedEntityState>(),
      props: type<EntityProps<Parcours> & { selectedEntity: Signal<Parcours | null> }>(),
    },
    withMethods((store) => ({
      ajouterParcours: (state: { parcours: Parcours }) => patchState(store, setEntity(state.parcours)),
      chargerParcours: (state: { parcours: Parcours[] }) => patchState(store, setAllEntities(state.parcours)),
      modifierParcours: (state: { parcours: Parcours }) =>
        patchState(store, updateEntity({ id: state.parcours.id, changes: state.parcours })),
    })),
    withMethods(
      (
        store,
        service = inject(RéférentielMétierParcoursService),
        groupeÉtapesStore = inject(GroupeÉtapesÀLaCarteStore),
      ) => ({
        charger: rxMethod<{
          idCartographie: string;
          idReferentiel?: string;
        }>(
          pipe(
            switchMap(({ idCartographie, idReferentiel }) =>
              service.trouverTous(idCartographie, idReferentiel).pipe(
                tapResponse({
                  next: (result) => store.chargerParcours({ parcours: result.items }),
                  error: (err) => {
                    console.error(err);
                    const toastrId = uuidv7();
                    toast.custom(ToastrErrorComponent, {
                      componentProps: {
                        message: `Un problème est survenu lors de la récupération des parcours, contactez le support.`,
                        id: toastrId,
                      },
                      id: toastrId,
                    });
                  },
                }),
              ),
            ),
          ),
        ),
        chargerParCartographie: rxMethod<string>(
          pipe(
            switchMap((idCartographie) =>
              service.trouverTous(idCartographie).pipe(
                tapResponse({
                  next: (result: Response<Parcours>) => {
                    patchState(store, { paginator: { ...store.paginator(), pageNb: 1 } });
                    store.chargerParcours({
                      parcours: result.items,
                    });
                  },
                  error: () => {
                    const toastrId = uuidv7();

                    toast.custom(ToastrErrorComponent, {
                      componentProps: {
                        message: 'Un problème est survenu lors du chargement des référentiels, contactez le support.',
                        titre: "Le chargement des cartographies n'a pas abouti",
                        id: toastrId,
                      },
                      id: toastrId,
                    });
                  },
                }),
              ),
            ),
          ),
        ),
        créer: rxMethod<{
          idReferentiel: string;
          nom: string;
          next: (parcours: Parcours) => void;
        }>(
          pipe(
            switchMap(({ nom, idReferentiel, next }) =>
              service.créer({ nom, idReferentiel }).pipe(
                tapResponse({
                  next: (result) => {
                    store.ajouterParcours({ parcours: result });
                    next(result);
                  },
                  error: (err) => {
                    console.error(err);
                    const toastrId = uuidv7();
                    toast.custom(ToastrErrorComponent, {
                      componentProps: {
                        message: `Un problème est survenu lors de la création du parcours, contactez le support.`,
                        id: toastrId,
                      },
                      id: toastrId,
                    });
                  },
                }),
              ),
            ),
          ),
        ),
        modifier: rxMethod<{
          nom: string;
          next: () => void;
        }>(
          pipe(
            switchMap(({ nom, next }) => {
              const parcours = store.selectedEntity();
              if (!parcours) throw Error();
              return service.modifier({ ...parcours, nom }).pipe(
                tapResponse({
                  next: () => {
                    store.modifierParcours({ parcours });
                    next();
                  },
                  error: (err) => {
                    console.error(err);
                    const toastrId = uuidv7();
                    toast.custom(ToastrErrorComponent, {
                      componentProps: {
                        message: `Un problème est survenu lors de la modification du parcours, contactez le support.`,
                        id: toastrId,
                      },
                      id: toastrId,
                    });
                  },
                }),
              );
            }),
          ),
        ),
        chargerConnecteurs: (connecteurs: Connecteur[]) => {
          const parcours = store.selectedEntity();
          if (!parcours) throw Error();
          parcours.connecteurs = connecteurs;
          store.modifierParcours({ parcours });
        },
        ajouterConnecteur: (nouveauConnecteur: Connecteur) => {
          const parcours = store.selectedEntity();
          if (!parcours) throw Error();
          // Vérifier si le lien existe déjà
          if (
            parcours.connecteurs.find(
              (x) =>
                `${x.idEtapePrecedente}--${x.idEtapeSuivante}--${x.idGroupeEtapesALaCartePrecedent}--${x.idGroupeEtapesALaCarteSuivant}` ===
                `${nouveauConnecteur.idEtapePrecedente}--${nouveauConnecteur.idEtapeSuivante}--${nouveauConnecteur.idGroupeEtapesALaCartePrecedent}--${nouveauConnecteur.idGroupeEtapesALaCarteSuivant}`,
            )
          )
            return;
          parcours.connecteurs.push(nouveauConnecteur);
          return store.modifierParcours({ parcours });
        },
        supprimerConnecteur: (connecteur: Connecteur) => {
          const parcours = store.selectedEntity();
          if (!parcours) throw Error();
          const idxConnecteur = parcours.connecteurs.findIndex(
            (c) =>
              c.idEtapePrecedente === connecteur.idEtapePrecedente &&
              c.idEtapeSuivante === connecteur.idEtapeSuivante &&
              c.idGroupeEtapesALaCartePrecedent === connecteur.idGroupeEtapesALaCartePrecedent &&
              c.idGroupeEtapesALaCarteSuivant === connecteur.idGroupeEtapesALaCarteSuivant,
          );
          if (idxConnecteur !== -1) parcours.connecteurs.splice(idxConnecteur, 1);

          return store.modifierParcours({ parcours });
        },
        ajouterJalon: (nouveauJalon: Jalon) => {
          const parcours = store.selectedEntity();
          if (!parcours) throw Error();
          parcours.jalons.push(nouveauJalon);
          return store.modifierParcours({ parcours });
        },
        supprimerFinParcours: (idFinParcours: string) => {
          const parcours = store.selectedEntity();
          if (!parcours) throw Error();
          const idxFinParcours = parcours.finsParcours.findIndex((c) => c.id === idFinParcours);
          if (idxFinParcours !== -1) {
            parcours.finsParcours.splice(idxFinParcours, 1);
          }
          return store.modifierParcours({ parcours });
        },
        supprimerJalon: (idJalon: string) => {
          const parcours = store.selectedEntity();
          if (!parcours) throw Error();
          const idxJalon = parcours.jalons.findIndex((c) => c.id === idJalon);
          if (idxJalon !== -1) {
            parcours.jalons.splice(idxJalon, 1);
          }
          return store.modifierParcours({ parcours });
        },
        ajouterFinParcours: (finParcours: FinParcours) => {
          const parcours = store.selectedEntity();
          if (!parcours) throw Error();
          parcours.finsParcours.push(finParcours);
          return store.modifierParcours({ parcours });
        },
        attribuerJalons: (jalons: Jalon[]) => {
          const parcours = store.selectedEntity();
          if (!parcours) throw Error();
          parcours.jalons = jalons;
          return store.modifierParcours({ parcours });
        },
        attribuerFinsParcours: (finsParcours: FinParcours[]) => {
          const parcours = store.selectedEntity();
          if (!parcours) throw Error();
          parcours.finsParcours = finsParcours;
          return store.modifierParcours({ parcours });
        },
        sauvegarderParcours: rxMethod<void>(
          pipe(
            switchMap(() => {
              const parcours = store.selectedEntity();
              if (!parcours) throw Error();
              return service.modifier(parcours).pipe(
                switchMap(() =>
                  service.trouverParId(parcours.id).pipe(
                    tapResponse({
                      next: (parcours: Parcours) => {
                        store.modifierParcours({ parcours });
                        groupeÉtapesStore.chargerGroupes({
                          groupes: parcours.groupesEtapesALaCarte,
                        });
                      },
                      error: (err) => {
                        console.error(err);
                        const toastrId = uuidv7();
                        toast.custom(ToastrErrorComponent, {
                          componentProps: {
                            message: `Un problème est survenu lors de la sauvegarde du parcours, contactez le support.`,
                            id: toastrId,
                          },
                          id: toastrId,
                        });
                      },
                    }),
                  ),
                ),
              );
            }),
          ),
        ),
        exportCartographie: rxMethod<{ idCartographie: string; next: (blob: Blob) => void; error: () => void }>(
          pipe(
            switchMap(({ idCartographie, next, error }) => {
              return service.exportCartographie(idCartographie).pipe(
                tapResponse({
                  next: (blob: Blob) => {
                    next(blob);
                  },
                  error: (err) => {
                    console.error(err);
                    const toastrId = uuidv7();
                    toast.custom(ToastrErrorComponent, {
                      componentProps: {
                        message: `Un problème est survenu lors de l'export de la cartographie, contactez le support.`,
                        id: toastrId,
                      },
                      id: toastrId,
                    });
                    error();
                  },
                }),
              );
            }),
          ),
        ),
        exportOneParcours: rxMethod<{
          idParcours: string;
          idCartographie: string;
          next: (blob: Blob) => void;
          error: () => void;
        }>(
          pipe(
            switchMap(({ idParcours, idCartographie, next, error }) => {
              return service.exportOneParcours(idParcours, idCartographie).pipe(
                tapResponse({
                  next: (blob: Blob) => {
                    next(blob);
                  },
                  error: (err) => {
                    console.error(err);
                    const toastrId = uuidv7();
                    toast.custom(ToastrErrorComponent, {
                      componentProps: {
                        message: `Un problème est survenu lors de l'export du parcours, contactez le support.`,
                        id: toastrId,
                      },
                      id: toastrId,
                    });
                    error();
                  },
                }),
              );
            }),
          ),
        ),
      }),
    ),
  );
}
